import { getTableMetaHelper } from '@round/ui-kit';
import { ProjectTableMeta } from '../types';
import { creatorbase } from '@round/api';
import { CellContext } from '@tanstack/react-table';
import { Badge, Skeleton, Tooltip } from '@round/creatorbase-ui';
import cn from 'classnames';
import useOverflowingChildren from 'Hooks/useOverflowingChildren';
import styles from './GenresCell.module.css';

// Room kept free at the right edge for the "+N" badge so genres never crowd it out of view.
const OVERFLOW_BADGE_RESERVE_PX = 40;

type Row = creatorbase.Project;
type Context<T extends Row> = CellContext<T, T['genres']>;

const getTableMeta = getTableMetaHelper<ProjectTableMeta<Row>>();

const GenresCell = <T extends Row>({ getValue, row: { original }, table }: Context<T>) => {
    const { getIsRowLoading } = getTableMeta(table);
    const isLoading = getIsRowLoading(original);
    // placeholder rows carry no value at all while the page is loading
    const genres = getValue() ?? [];
    const { ref, overflowingChildren } = useOverflowingChildren<HTMLDivElement>({
        reserveRight: OVERFLOW_BADGE_RESERVE_PX,
    });
    const overflowCount = overflowingChildren.length;

    if (!isLoading && !genres.length) {
        return <span className={styles.muted}>-</span>;
    }

    return (
        <Tooltip>
            <Tooltip.Trigger asChild>
                {/* asChild keeps the native div, which Radix does not make focusable on its own */}
                <div ref={ref} tabIndex={0} className={styles.container}>
                    {isLoading
                        ? Array(3)
                              .fill({})
                              .map((_, i) => <Skeleton className={styles.badgeSkeleton} key={i} />)
                        : genres.map((genre) => (
                              <Badge key={genre.value} variant="outline" className={styles.badge}>
                                  {genre.label}
                              </Badge>
                          ))}
                    {overflowCount > 0 && (
                        <Badge data-overflowing="ignore" variant="outline" className={cn(styles.badge, styles.muted)}>
                            +{overflowCount}
                        </Badge>
                    )}
                </div>
            </Tooltip.Trigger>
            <Tooltip.Content side="bottom" className={styles.tooltipContent}>
                <p>{genres.map((genre) => genre.label).join(', ')}</p>
            </Tooltip.Content>
        </Tooltip>
    );
};

export default GenresCell;
